import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2, CheckCircle, ShieldCheck } from "lucide-react";
import { PublicPageCopy } from "../components/PublicPageCopy";
import { formatApiError } from '../utils/errorFormatter';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      // For testing purposes - in production, remove this
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success("Reset instructions sent!");
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(formatApiError(error, "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">The HospiceGuard</h1>
          </div>
        </div>
        <div className="space-y-6">
          <h2 className="text-4xl font-bold text-white leading-tight">
            Reset Your Password
          </h2>
          <p className="text-emerald-100 text-lg">
            We'll help you get back into your account securely.
          </p>
        </div>
        <p className="text-emerald-200 text-sm">
          The HospiceGuard Hospice Audit System - HIPAA Compliant
        </p>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ShieldCheck className="w-8 h-8 text-primary" />
              <h1 className="text-2xl font-bold">The HospiceGuard</h1>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl">Forgot Password</CardTitle>
              <CardDescription>
                {submitted 
                  ? "Check your email for reset instructions" 
                  : "Enter your email to receive reset instructions"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h3 className="font-semibold text-lg">Check Your Email</h3>
                    <p className="text-muted-foreground text-sm mt-2">
                      If an account exists for {email}, you will receive password reset instructions.
                    </p>
                  </div>

                  {/* For testing - show reset link */}
                  {resetToken && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <p className="text-sm text-amber-600 font-medium mb-2">Testing Mode</p>
                      <Link 
                        to={`/reset-password?token=${resetToken}`}
                        className="text-sm text-primary hover:underline break-all"
                      >
                        Click here to reset password
                      </Link>
                    </div>
                  )}

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setSubmitted(false);
                      setEmail("");
                      setResetToken(null);
                    }}
                  >
                    Try Another Email
                  </Button>
                </div>
              )}

              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    <PublicPageCopy page="forgot-password" withSeo />
    </>
  );
};

export default ForgotPasswordPage;
